import { ActionIcon, Stack, Text, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { CheckIcon, PenIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";

import { DataGridBase } from "@/components/DataGridBase";
import { Plate } from "@/components/Plate";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { MODAL_HANDLER } from "@/lib";
import { QueueRestore, Role, useGetQueueRestoresQuery, useSaveQueueRestoreDeleteMutation, useSaveQueueRestoreMutation } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/resgate/")({
  component: RouteComponent,
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

function RouteComponent() {
  const { isAdminRoot } = useSession();
  const {
    data = [],
    isFetching,
    refetch
  } = useGetQueueRestoresQuery(undefined, {
    selectFromResult: ({ data, ...state }) => {
      // Clonamos o array com [...data] antes de aplicar o .sort()
      const sortedData = data ? [...data].sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt))) : [];

      return {
        ...state,
        data: sortedData
      };
    }
  });
  const [triggerDelete, { isLoading: isDeleting }] = useSaveQueueRestoreDeleteMutation();
  const [trigger, { isLoading }] = useSaveQueueRestoreMutation();

  const columns: GridColDef<QueueRestore>[] = [
    { field: "id", headerName: "ID", width: 75, align: "center", headerAlign: "center", type: "number" },
    {
      field: "plate",
      valueGetter: (val, { truck }) => truck.licensePlate,
      headerName: "Placa",
      flex: 1,
      minWidth: 175,
      type: "custom",
      display: "flex",
      renderCell: ({ row }) => {
        const { truck } = row;
        return <Plate plate={truck.licensePlate} />;
      }
    },
    {
      field: "driver",
      valueGetter: (val, { driver }) => driver.name,
      headerName: "Motorista",
      display: "flex",
      flex: 1,
      type: "custom",
      minWidth: 250,
      renderCell: ({ row }) => {
        const { driver, qtyTransported } = row;
        return (
          <div className="flex flex-col">
            <Text>{driver.name}</Text>
            <Text size="xs" c="dimmed" fw="bold">
              Peso: {qtyTransported}
            </Text>
          </div>
        );
      }
    },
    {
      field: "company",
      valueGetter: (val, { company }) => company.name,
      headerName: "Transportadora",
      flex: 1,
      type: "string",
      minWidth: 150
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      minWidth: 150,
      display: "flex",
      renderCell: ({ row }) => {
        const { id, status, company, truck, qtyTransported, order, orderId, queueId } = row;

        const handleApproveRescue = async () => {
          modals.openConfirmModal({
            title: "Resgate",
            children: (
              <>
                <Plate plate={truck.licensePlate} color="text-blue-500" />
                <div className="mt-2">
                  <Text size="lg">Peso: {qtyTransported}</Text>
                  <Text size="lg">Pedido: {order.code}</Text>
                </div>
              </>
            ),
            groupProps: {
              className: cn("grid! grid-cols-1 gap-2 md:grid-cols-2")
            },
            confirmProps: {
              color: "green",
              loading: isLoading
            },
            cancelProps: {
              variant: "default",
              disabled: isLoading
            },
            labels: {
              cancel: "Cancelar",
              confirm: "Resgatar"
            },
            onConfirm: () => {
              trigger({
                id,
                company: {
                  id: String(company.id)
                }
              });
            }
          });
        };

        const handleEditRescue = () => {
          MODAL_HANDLER.edit.queueRestore({ id, orderId, queueId, qtyTransported: String(qtyTransported), company: { id: company.id } });
        };

        const handleDeleteRescue = () => {
          modals.openConfirmModal({
            title: "Excluir Resgate",
            children: <Text>Tem certeza que deseja excluir este resgate?</Text>,
            groupProps: {
              gap: "xs",
              className: cn("grid! grid-cols-1 md:grid-cols-2")
            },
            confirmProps: {
              color: "red",
              loading: isDeleting
            },
            cancelProps: {
              variant: "default",
              disabled: isDeleting
            },
            labels: {
              cancel: "Cancelar",
              confirm: "Excluir"
            },
            onConfirm: () => {
              triggerDelete(id);
            }
          });
        };

        return (
          <ActionIcon.Group>
            {!status && (
              <Tooltip label="Aprovar resgate">
                <ActionIcon color="green" variant="light" radius="xs" onClick={handleApproveRescue}>
                  <CheckIcon weight="bold" />
                </ActionIcon>
              </Tooltip>
            )}

            {isAdminRoot && (
              <>
                <Tooltip label="Editar resgate">
                  <ActionIcon color="yellow" variant="light" radius="xs" onClick={handleEditRescue}>
                    <PenIcon weight="bold" />
                  </ActionIcon>
                </Tooltip>

                <Tooltip label="Excluir resgate">
                  <ActionIcon color="red" variant="light" radius="xs" onClick={handleDeleteRescue}>
                    <TrashIcon weight="bold" />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <DashboardComponents.Title>Resgates</DashboardComponents.Title>

        <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Resgates" }} onClick={refetch} loading={isFetching} />
      </div>

      <DataGridBase rows={data} columns={columns} loading={isFetching} />

      {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
    </Stack>
  );
}
